/**
 * The route contract map: every route of docs/API.md declared once.
 * `services/api` implements it (Hono + @hono/zod-openapi) and `apps/web`
 * builds its typed client from it. Paths are relative to `API_BASE` and use
 * Hono-style `:param` segments.
 */
import type { z } from 'zod'
import type { RouteDef } from './routes/define.js'
import { publicRoutes } from './routes/public.js'
import { agentRoutes } from './routes/agents.js'
import { tradingRoutes } from './routes/trading.js'
import { milestone3Routes } from './routes/milestone3.js'
import { creationRoutes } from './routes/creation.js'
import { socialRoutes } from './routes/social.js'
import { venueRoutes } from './routes/venues.js'
import { deckRoutes } from './routes/deck.js'
import { externalAgentRoutes } from './routes/external-agents.js'
import { conversationRoutes } from './routes/conversations.js'
import { productRoutes } from './routes/product.js'
import { roomRoutes } from './routes/rooms.js'

export { API_BASE, type HttpMethod, type RouteDef, defineRoute } from './routes/define.js'
/** The rooms' routes alone (lane CHATCORE1), for code that handles or mocks exactly them. */
export { roomRoutes } from './routes/rooms.js'
export { FeedQuery, StreamQuery, ListAgentsQuery, ListTokensQuery, MyProposalsQuery } from './routes/params.js'

// The map ----------------------------------------------------------------------------

export const routes = {
  ...publicRoutes,
  ...creationRoutes,
  ...agentRoutes,
  ...tradingRoutes,
  ...milestone3Routes,
  ...socialRoutes,
  ...venueRoutes,
  ...deckRoutes,
  ...externalAgentRoutes,
  ...conversationRoutes,
  ...productRoutes,
  ...roomRoutes,
} as const satisfies Record<string, RouteDef>

// Type helpers -------------------------------------------------------------------------

export type Routes = typeof routes
export type RouteName = keyof Routes
export type Route<N extends RouteName> = Routes[N]

type SchemaInput<R, K extends string> = R extends { [P in K]: infer S extends z.ZodType } ? z.input<S> : undefined
type SchemaOutput<R, K extends string> = R extends { [P in K]: infer S extends z.ZodType } ? z.output<S> : undefined

/** Client-side (pre-parse) shapes. */
export type RouteParamsInput<N extends RouteName> = SchemaInput<Routes[N], 'params'>
export type RouteQueryInput<N extends RouteName> = SchemaInput<Routes[N], 'query'>
export type RouteBodyInput<N extends RouteName> = SchemaInput<Routes[N], 'body'>
/** Server-side (post-parse) shapes. */
export type RouteParams<N extends RouteName> = SchemaOutput<Routes[N], 'params'>
export type RouteQuery<N extends RouteName> = SchemaOutput<Routes[N], 'query'>
export type RouteBody<N extends RouteName> = SchemaOutput<Routes[N], 'body'>
/** Response body (for stream routes: one SSE event's data). */
export type RouteResponse<N extends RouteName> = z.output<Routes[N]['response']>

type DropUndefined<T> = { [K in keyof T as T[K] extends undefined ? never : K]: T[K] }
/** What a typed client passes for route N: `{ params?, query?, body? }` with only the relevant keys. */
export type RouteRequest<N extends RouteName> = DropUndefined<{
  params: RouteParamsInput<N>
  query: RouteQueryInput<N>
  body: RouteBodyInput<N>
}>

export const routeNames = Object.keys(routes) as RouteName[]
export const routeList: (RouteDef & { name: RouteName })[] = routeNames.map((name) => ({ ...routes[name], name }))

/** `:param` names in a path, in order. */
export function pathParamNames(path: string): string[] {
  return [...path.matchAll(/:([A-Za-z_][A-Za-z0-9_]*)/g)].map((m) => m[1]!)
}

/** Fill `:params` in a route path. Returns the path relative to API_BASE. */
export function buildPath(name: RouteName, params: Record<string, string> = {}): string {
  return routes[name].path.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_, k: string) => {
    const v = params[k]
    if (v === undefined) throw new Error(`missing path param ${k} for route ${name}`)
    return encodeURIComponent(v)
  })
}

/** Convert a Hono-style path to OpenAPI style (`/agents/{handle}`). */
export function toOpenApiPath(path: string): string {
  return path.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, '{$1}')
}
